import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ConfigService } from '../config/config.service';
import { LogicService } from '../logic/logic.service';
import { StorageInfoService } from '../storage-info/storage-info.service';
import { StorageService } from '../storage/storage.service';
import { StringVariableService } from '../string-variable/string-variable.service';
import { ValidationService } from '../validation/validation.service';

import { dependencies } from '../../class/workflow.dependency';
import { Backend } from '../../class/workflow.type';
import { WorkflowDictionaryService } from '../workflow-dictionary/workflow-dictionary.service';
import { extendConfigs } from '../extend-config/extend-config.service';
import { WorkflowWidgetEventServiceService } from '../../widget/workflow-widget-event.service';
import { StateManagementService } from '../state-management/state-management.service';
import { FieldService } from '@workflow/class/field.service';
import { CardService } from '@workflow/class/card.service';
import { PageService } from '@workflow/class/page.service';
import { FlowService } from '@workflow/class/flow.service';
import { StatefulService } from '@app/service/stateful.service';

/**
 * A service responsible to initialize workflow dependencies
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowInitializeService {
  constructor(
    private readonly configService: ConfigService,
    private readonly logicService: LogicService,
    private readonly dictionaryService: WorkflowDictionaryService,
    private readonly httpClient: HttpClient,
    private readonly validationService: ValidationService,
    private readonly storageInfoService: StorageInfoService,
    private readonly storageService: StorageService,
    private readonly stringVariableService: StringVariableService,
    private readonly widgetEventService: WorkflowWidgetEventServiceService,
    private readonly stateManagementService: StateManagementService,
    private readonly fieldService: FieldService,
    private readonly cardService: CardService,
    private readonly pageService: PageService,
    private readonly flowService: FlowService,
    private readonly statefulService: StatefulService
  ) {}

  public initAllDependencies(): void {
    this.initServices();
    this.initConfigs();
    this.initBackend();
    this.initLogicServices();
  }

  public initServices(): void {
    dependencies.configService = this.configService;
    dependencies.httpClient = this.httpClient;
    dependencies.storageInfoService = this.storageInfoService;
    dependencies.storageService = this.storageService;
    dependencies.dictionaryService = this.dictionaryService;
    dependencies.logicService = this.logicService;
    dependencies.stringVariableService = this.stringVariableService;
    dependencies.validationService = this.validationService;
    dependencies.widgetEventService = this.widgetEventService;
    dependencies.stateManagementService = this.stateManagementService;
  }

  public initConfigs(): void {
    const configSrc = this.configService.config;
    dependencies.config = {
      apiEnums: configSrc['api-enums'] || {},
      cards: configSrc['workflow-cards'] || {},
      enums: configSrc['workflow-enums'] || {},
      fields: configSrc['workflow-fields'] || {},
      flows: configSrc.workflows || {},
      pages: configSrc['workflow-pages'] || {},
      storage: configSrc.storage || {},
      workflowSpecificCards: configSrc['workflow-specific-cards'] || {},
      workflowSpecificFields: configSrc['workflow-specific-fields'] || {},
      workflowSpecificPages: configSrc['workflow-specific-pages'] || {}
    };

    this.addBackend();
    this.ensureConfigs();
    this.extendsAllConfigs();
    this.testConfigs();
  }

  public extendsAllConfigs() {
    extendConfigs(dependencies.config.fields);
    extendConfigs(dependencies.config.cards);
    extendConfigs(dependencies.config.pages);
    extendConfigs(dependencies.config.flows);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public initBackend(): Promise<any> {
    return this.storageService.initAllBackends();
  }

  public initLogicServices(): void {
    this.logicService.register(this.validationService);
  }

  public testConfigs(): void {
    // test config entries
    (['api', 'mem', 'ss'] as Backend[]).forEach((backendType) => {
      dependencies.config.storage[backendType] = dependencies.config.storage[backendType] || {};
      Object.keys(dependencies.config.storage[backendType]).forEach((id) => {
        this.statefulService.test(backendType, id);
      });
    });
    Object.keys(dependencies.config.fields).forEach((id) => {
      this.fieldService.test(id);
    });
    Object.keys(dependencies.config.cards).forEach((id) => {
      this.cardService.test(id);
    });
    Object.keys(dependencies.config.pages).forEach((id) => {
      this.pageService.test(id);
    });
    Object.keys(dependencies.config.flows).forEach((id) => {
      this.flowService.test(id);
    });
  }

  public ensureConfigs(): void {
    Object.keys(dependencies.config.fields).forEach((id) => {
      this.fieldService.ensureConfig(id);
    });
    Object.keys(dependencies.config.cards).forEach((id) => {
      this.cardService.ensureConfig(id);
    });
    Object.keys(dependencies.config.pages).forEach((id) => {
      this.pageService.ensureConfig(id);
    });
    Object.keys(dependencies.config.flows).forEach((id) => {
      this.flowService.ensureConfig(id);
    });
  }

  private addBackend(): void {
    const backend = this.configService.config['storage-info']?.backend;
    for (const [protocol, { type, endpoint, token }] of Object.entries(backend || {})) {
      this.storageService.addBackend(this.statefulService, protocol, type, endpoint, token);
    }
  }
}
